// src/components/SurgeryRpPanel.tsx (2026-07-18 新設・ベータ)
// 手術売上: ⓪月次推移の縦積み上げ棒 (直近12ヶ月・手技/材料薬剤) ①診療科別の売上構成横棒
//   ②手術別TOP_N/選択術式の売上構成横棒 ③手術別の金額分布。
//   源 = fact_surgery_rp (会計RP請求金額付与PJ。手術+手術材料+手術薬剤・輸血/麻酔除外・科=入院主科=手術日の046在院科)。
//   金額 = 円 (会計明細円)。労災RPも円のまま合計に含む (円軸なら正・点換算の過大なし)。
//   マスタ点数のみ「点」(一般の点数)。/件 = 中央値[四分位]。既定 = 最新月1ヶ月・手技金額/合計 降順。
//   [2026-08-21 ユーザ指示] ⓪を最上部に追加。選択期間の外側も見えるよう直近12ヶ月を常に描き、
//   選択中 (開始月〜終了月) の x 軸ラベルだけ赤字にして「いま見ている月」を示す。
//   印刷 = A4横 (styles.css .opesr-*)。
import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useFilters, monthShift, type OpeRevenueMode } from "../store";
import { useSort, SortTh } from "../lib/useSort";
import { CsvButton } from "./CsvButton";
import { ContextBar } from "./ContextBar";
import type {
  SurgeryRpResponse, SurgeryRpRow, SurgeryRpSummaryRow, SurgeryRpProcedureMonthlyRow,
  MetricRow,
} from "@shared/api";

const i0 = (v: number | null | undefined) => v == null ? "—" : Math.round(v).toLocaleString();
// 件数 (月平均) の書式。1ヶ月選択なら実数そのものなので整数、複数月なら小数1桁。
const caseCount = (v: number | null | undefined, months: number) =>
  v == null ? "—" : months === 1 ? Math.round(v).toLocaleString() : v.toFixed(1);
const chartAmount = (value: number, rowTotal: number) => {
  const useOku = rowTotal >= 100_000_000;
  const scaled = value / (useOku ? 100_000_000 : 10_000);
  return `${scaled.toLocaleString("ja-JP", {
    minimumFractionDigits: useOku ? 2 : 0,
    maximumFractionDigits: useOku ? 2 : 0,   // [2026-08-21] 億は小数2位まで
  })}${useOku ? "億円" : "万円"}`;
};

function MedIqr({ med, q1, q3 }: { med: number; q1: number; q3: number }) {
  return (
    <span className="whitespace-nowrap">
      <b className="tabular-nums">{i0(med)}</b>
      <span className="text-slate-400 text-[10px] ml-0.5">[{i0(q1)}–{i0(q3)}]</span>
    </span>
  );
}

type RevenueChartRow = {
  key: string;
  label: string;
  n?: number;
  total_en: number;
  gi_en: number;
  zai_en: number;
  yaku_en: number;
  isHospital?: boolean;
};

const OPE_REVENUE_SEGMENTS = [
  { key: "gi", label: "手技", color: "#1B9E77", text: "text-white", value: (r: RevenueChartRow) => Math.max(0, r.gi_en) },
  {
    key: "zai_yaku",
    label: "材料・薬剤",
    color: "#386FA4",
    text: "text-white",
    value: (r: RevenueChartRow) => Math.max(0, r.zai_en) + Math.max(0, r.yaku_en),
  },
] as const;
type RevenueSegment = (typeof OPE_REVENUE_SEGMENTS)[number];
type ProcedureAmountBasis = "perCase" | "total";

// ── ⓪ 月次推移 縦積み上げ棒 (2026-08-21 ユーザ指示・行為別売上タブの月次グラフと同じ流儀) ──
//   x = 年月 (直近12ヶ月。選択期間が12ヶ月より長ければ期間全体)、y = 実額 (円)、
//   セグメント = 手技 / 材料・薬剤。選択中の期間 (開始月〜終了月) は x 軸ラベルを赤字にする。
const ymLabel = (ym: string) => `${ym.slice(2, 4)}年${ym.slice(4, 6)}月`;
const MONTHLY_CHART_H = 208;   // 棒の描画領域 (px)。セグメント内テキストを出すかの判定に使う

// ★手術別グラフ / 件数の推移に出す上位件数。
//   2026-09-18 に一度 15 へ落としたが、画面の説明文を「このタブの見方」へ移し、印刷の行間を詰めた結果
//   **20 でも A4 横 1 ページに収まる**ようになったので 20 に戻した (ユーザ指摘「TOP20まで行けそう」)。
//   A4 横の印刷領域 (1085 × 756px) での実測 (期間 5ヶ月・全科と主要科):
//     ③月次推移 + ④件数の推移 … 最悪 = 整形外科 705px (93%)。脳センター 617px / 心外 701px。
//     ①診療科別 + ②手術別 (科フォーカス時) … 607〜615px (80%)。
//   ★余裕を決めているのは**名称が長い診療科**(整形外科は脊椎固定術など 2 行に折り返す行が多い)。
//   増やすときは必ず整形外科で測ること。
const TOP_N = 20;

function MonthlyRevenueChart({
  rows, selFrom, selTo, revenueMode, onRevenueModeChange, scopeLabel, isLoading,
}: {
  rows: RevenueChartRow[];      // key = "YYYYMM" (昇順)
  selFrom: string;              // 選択期間の開始月 "YYYYMM"
  selTo: string;                // 選択期間の終了月 "YYYYMM"
  revenueMode: OpeRevenueMode;
  onRevenueModeChange: (mode: OpeRevenueMode) => void;
  scopeLabel: string;           // "病院全体" / 科名 / "○科 他n科"
  isLoading: boolean;
}) {
  const handOnly = revenueMode === "手技売上";
  const materialOnly = revenueMode === "材料薬剤費";
  const focusedSegment = handOnly
    ? OPE_REVENUE_SEGMENTS[0]
    : materialOnly
      ? OPE_REVENUE_SEGMENTS[1]
      : null;
  const segments: readonly RevenueSegment[] = focusedSegment ? [focusedSegment] : OPE_REVENUE_SEGMENTS;
  const displayedTotal = (r: RevenueChartRow) => Math.max(
    0,
    handOnly ? r.gi_en : materialOnly ? r.zai_en + r.yaku_en : r.total_en,
  );
  const maxTotal = Math.max(1, ...rows.map(displayedTotal));
  // 月次は「前月とどれだけ違うか」を読む図なので、単位をグラフ内で固定し小数位を必ず出す
  //   (chartAmount の値ごと自動切替だと 2.0/2.1 億が両方「2億円」に丸まって推移が消える)。
  //   [2026-08-21 ユーザ指示] 億は小数2位まで。
  const amt = (v: number) => maxTotal >= 100_000_000
    ? `${(v / 100_000_000).toFixed(2)}億円`
    : `${Math.round(v / 10_000).toLocaleString()}万円`;
  const heading = handOnly ? "手技売上" : materialOnly ? "材料薬剤費" : "手術売上";
  // 月数が多いときは x 軸ラベルを縮める (12ヶ月なら余裕がある)
  const denseLabel = rows.length > 14;

  return (
    <>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h3 className="text-sm font-bold">
          📊 {heading} 月次推移
          <span className="text-[11px] font-normal text-slate-500 ml-1">
            直近{rows.length}ヶ月
          </span>
        </h3>
        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700">{scopeLabel}</span>
        <span className="text-[10px] text-red-600 font-semibold">赤字の年月 = 選択中の期間</span>
        {isLoading && <span className="text-[10px] text-slate-400 no-print">loading…</span>}
        <div className="ml-auto flex items-center gap-3 text-[10px] text-slate-600 flex-wrap">
          {segments.map((seg) => (
            <span key={seg.key} className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: seg.color }} />
              {seg.label}
            </span>
          ))}
          <span className="text-slate-400">y = 売上実額 (円)</span>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-8">該当データなし</div>
      ) : (
        <>
          <div className="opesr-monthly-chart flex items-end gap-[3px] border-b border-slate-300"
            style={{ height: MONTHLY_CHART_H }}>
            {rows.map((r) => {
              const tot = displayedTotal(r);
              const tip = [
                `${ymLabel(r.key)}: ${amt(tot)}`,
                ...segments.map((seg) => {
                  const v = seg.value(r);
                  return `${seg.label}: ${amt(v)} (${tot > 0 ? ((v / tot) * 100).toFixed(1) : "0.0"}%)`;
                }),
                `手術会計件数: ${Math.round(r.n ?? 0).toLocaleString()}件 (この月)`,
              ].join("\n");
              return (
                <div key={r.key} className="flex-1 min-w-0 flex flex-col items-center justify-end h-full" title={tip}>
                  <span className="text-[10px] font-semibold text-slate-700 leading-none mb-0.5 whitespace-nowrap">
                    {tot > 0 ? amt(tot) : ""}
                  </span>
                  <div className="w-full flex flex-col-reverse rounded-t overflow-hidden"
                    style={{ height: `${(tot / maxTotal) * 100}%` }}>
                    {segments.map((seg) => {
                      const v = seg.value(r);
                      if (v <= 0 || tot <= 0) return null;
                      const segPx = (v / maxTotal) * MONTHLY_CHART_H;
                      const pct = (v / tot) * 100;
                      const segmentMode: OpeRevenueMode = seg.key === "gi" ? "手技売上" : "材料薬剤費";
                      const nextMode: OpeRevenueMode = revenueMode === segmentMode ? "手術売上" : segmentMode;
                      return (
                        <button key={seg.key} type="button"
                          className={`w-full flex flex-col items-center justify-center overflow-hidden whitespace-nowrap border-0 p-0 cursor-pointer hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white ${seg.text}`}
                          style={{ height: `${pct}%`, background: seg.color }}
                          title={`${ymLabel(r.key)} ${seg.label}: ${amt(v)} (${pct.toFixed(1)}%)`}
                          aria-label={`${ymLabel(r.key)} ${seg.label} ${amt(v)}、${pct.toFixed(1)}%。${nextMode === "手術売上" ? "手術売上に戻す" : `${nextMode}に切り替える`}`}
                          onClick={() => onRevenueModeChange(nextMode)}>
                          {segPx >= 16 && <span className="text-[10px] font-semibold leading-tight">{amt(v)}</span>}
                          {segments.length > 1 && segPx >= 30 && <span className="text-[10px] leading-tight">{pct.toFixed(0)}%</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {/* x 軸 = 年月。選択中の期間 (開始月〜終了月) だけ赤字にして「いま見ている月」を示す */}
          <div className="opesr-monthly-axis flex gap-[3px] mt-1">
            {rows.map((r, i) => {
              const selected = r.key >= selFrom && r.key <= selTo;
              // 月数が多いときは「M月」だけにし、年が変わる 1 月と先頭だけ年を残す
              const showYear = !denseLabel || i === 0 || r.key.slice(4, 6) === "01";
              return (
                <div key={r.key}
                  className={`flex-1 min-w-0 text-center leading-tight whitespace-nowrap ${
                    denseLabel ? "text-[9px]" : "text-[11px]"
                  } ${selected ? "text-red-600 font-bold" : "text-slate-600"}`}
                  title={`${ymLabel(r.key)}${selected ? " (選択中の期間)" : ""}`}>
                  {showYear ? ymLabel(r.key) : `${Number(r.key.slice(4, 6))}月`}
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

function RevenueStackChart({
  rows,
  title,
  description,
  selectedKeys,
  onSelect,
  revenueMode,
  onRevenueModeChange,
  valueBasis,
  onValueBasisChange,
  countMonths,
  wideLabels = false,
  showCount = false,
}: {
  rows: RevenueChartRow[];
  title: string;
  description: string;
  selectedKeys: ReadonlySet<string>;
  onSelect: (key: string, additive: boolean) => void;
  revenueMode: OpeRevenueMode;
  onRevenueModeChange: (mode: OpeRevenueMode) => void;
  valueBasis?: ProcedureAmountBasis;
  onValueBasisChange?: (basis: ProcedureAmountBasis) => void;
  countMonths?: number;   // 件数を月平均で出すための月数 (1 なら実数表示)
  wideLabels?: boolean;
  showCount?: boolean;
}) {
  const [mode, setMode] = useState<"amount" | "percent">("percent");
  const handOnly = revenueMode === "手技売上";
  const materialOnly = revenueMode === "材料薬剤費";
  const focusedSegment = handOnly
    ? OPE_REVENUE_SEGMENTS[0]
    : materialOnly
      ? OPE_REVENUE_SEGMENTS[1]
      : null;
  const segments: readonly RevenueSegment[] = focusedSegment
    ? [focusedSegment]
    : OPE_REVENUE_SEGMENTS;
  const displayedTotal = (r: RevenueChartRow) => Math.max(
    0,
    handOnly
      ? r.gi_en
      : materialOnly
        ? r.zai_en + r.yaku_en
        : r.total_en,
  );
  const maxTotal = Math.max(1, ...rows.map(displayedTotal));
  const amountHeadingBase = handOnly
    ? "手技金額"
    : materialOnly
      ? "材料薬剤費"
      : "手術総金額";
  // 見出しは「主ラベル + 小さい単位ラベル」の 2 行（列名が長くなるのを避ける）
  const amountUnitLabel = valueBasis === "perCase" ? "1件あたり" : "月平均";
  const amountHeading = valueBasis === "perCase"
    ? `${amountHeadingBase}/件`
    : `${amountHeadingBase}(月平均)`;
  const gridCols = showCount
    ? "grid-cols-[22rem_minmax(18rem,1fr)_6.5rem_3.5rem]"
    : wideLabels
      ? "grid-cols-[22rem_minmax(18rem,1fr)_6.5rem]"
      : "grid-cols-[7rem_minmax(18rem,1fr)_6.5rem]";

  // 単一項目の100%棒は全行が同じ長さになるため、切替時は比較しやすい金額表示にする。
  useEffect(() => {
    if (focusedSegment) setMode("amount");
  }, [focusedSegment]);
  useEffect(() => {
    if (valueBasis === "perCase") setMode("amount");
  }, [valueBasis]);

  return (
    <>
      <div className="flex items-start gap-3 mb-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold">{title}</h3>
          <p className="opesr-chart-description text-[10px] text-slate-500 mt-0.5">
            {description}
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-slate-600 flex-wrap">
          {segments.map((seg) => (
            <span key={seg.key} className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: seg.color }} />
              {seg.label}
            </span>
          ))}
        </div>
        {valueBasis && onValueBasisChange && (
          <div className="opesr-chart-value-basis no-print ml-auto inline-flex rounded-md border border-slate-300 bg-white p-0.5"
            aria-label="手術別金額の集計切替">
            {([
              ["perCase", "1件あたり"],
              ["total", "月平均"],
            ] as const).map(([value, label]) => (
              <button key={value} type="button" onClick={() => onValueBasisChange(value)}
                aria-pressed={valueBasis === value}
                className={`px-2.5 py-1 rounded text-xs transition ${
                  valueBasis === value
                    ? "bg-emerald-600 text-white font-semibold shadow-sm"
                    : "text-slate-600 hover:bg-slate-50"
                }`}>
                {label}
              </button>
            ))}
          </div>
        )}
        <div className={`opesr-chart-mode no-print inline-flex rounded-md border border-slate-300 bg-white p-0.5 ${
          valueBasis ? "" : "ml-auto"
        }`}
          aria-label="手術売上グラフの表示切替">
          {([
            ["amount", "積み上げ"],
            ["percent", "100%積み上げ"],
          ] as const).map(([value, label]) => (
            <button key={value} type="button" onClick={() => setMode(value)}
              aria-pressed={mode === value}
              className={`px-2.5 py-1 rounded text-xs transition ${
                mode === value
                  ? "bg-emerald-600 text-white font-semibold shadow-sm"
                  : "text-slate-600 hover:bg-slate-50"
              }`}>
              {label}
            </button>
          ))}
        </div>
        <span className="hidden print:inline ml-auto text-[10px] text-slate-500">
          表示: {mode === "amount" ? "積み上げ" : "100%積み上げ"}
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-8">該当データなし</div>
      ) : (
        <div className="overflow-x-auto">
          <div className={wideLabels ? "min-w-[1000px]" : "min-w-[720px]"}>
            <div className={`opesr-chart-grid grid ${gridCols} items-end gap-2 px-2 pb-1 text-[10px] text-slate-400 border-b border-slate-200`}>
              <span>{wideLabels ? "手術" : "診療科"}</span>
              <span>{mode === "amount" ? "金額スケール" : "構成比スケール"}</span>
              <span className="text-right">
                {amountHeadingBase}<br />
                <span className="text-[9px] font-normal text-slate-400">{amountUnitLabel}</span>
              </span>
              {showCount && <span className="text-right">件数<br /><span className="text-[9px] font-normal text-slate-400">月平均</span></span>}
            </div>
            <div className="divide-y divide-slate-100">
              {rows.map((r) => {
                const rowTotal = displayedTotal(r);
                const segmentTotal = segments.reduce(
                  (sum, seg) => sum + seg.value(r),
                  0,
                );
                const barWidth = mode === "percent"
                  ? 100
                  : Math.max(0, Math.min(100, (rowTotal / maxTotal) * 100));
                const segmentAria = segments.map((seg) => {
                  const value = seg.value(r);
                  const pct = segmentTotal > 0 ? (value / segmentTotal) * 100 : 0;
                  return `${seg.label}${chartAmount(value, rowTotal)}、${pct.toFixed(1)}%`;
                }).join("、");
                return (
                  <div key={r.key}
                    className={`opesr-chart-row grid ${gridCols} gap-2 items-center px-2 py-1.5 ${
                      r.isHospital ? "bg-slate-50/80" : selectedKeys.has(r.key) ? "bg-amber-50" : ""
                    }`}>
                    <button type="button"
                      onClick={(e: ReactMouseEvent<HTMLButtonElement>) => onSelect(r.key, e.ctrlKey || e.metaKey)}
                      className={`opesr-chart-label text-left hover:text-emerald-700 hover:underline ${
                        wideLabels
                          ? `${r.label.length > 36 ? "text-[11px]" : "text-xs"} whitespace-normal break-words leading-tight`
                          : "text-xs truncate"
                      } ${
                        r.isHospital || selectedKeys.has(r.key) ? "font-bold text-slate-900" : "text-slate-700"
                      }`}
                      title="通常クリックで1件選択、Ctrl/⌘+クリックで複数選択">
                      {selectedKeys.has(r.key) && "✓ "}{r.label}
                    </button>
                    <div className="min-w-0">
                      <div className="opesr-chart-track h-8 rounded bg-slate-100 border border-slate-200 overflow-hidden"
                        role="group"
                        aria-label={`${r.label}: ${segmentAria}、${amountHeading}${i0(rowTotal)}円`}>
                        <div className="h-full flex rounded overflow-hidden transition-[width] duration-200"
                          style={{ width: `${barWidth}%` }}>
                          {segments.map((seg) => {
                            const value = seg.value(r);
                            const pct = segmentTotal > 0 ? (value / segmentTotal) * 100 : 0;
                            const visibleWidth = mode === "percent" ? pct : barWidth * pct / 100;
                            const amountLabel = chartAmount(value, rowTotal);
                            if (value <= 0) return null;
                            const segmentContent = visibleWidth >= 16 ? (
                              <span className="text-[10px] font-semibold">
                                {amountLabel}({pct.toFixed(0)}%)
                              </span>
                            ) : visibleWidth >= 7 ? (
                              <span className="text-[10px] font-semibold">{amountLabel}</span>
                            ) : null;
                            const segmentClass = `h-full flex items-center justify-center overflow-hidden whitespace-nowrap ${seg.text}`;
                            const segmentStyle = { width: `${pct}%`, background: seg.color };
                            const segmentTitle = `${r.label} ${seg.label}: ${amountLabel} (${pct.toFixed(1)}%)`;
                            const segmentMode: OpeRevenueMode = seg.key === "gi" ? "手技売上" : "材料薬剤費";
                            const nextMode: OpeRevenueMode = revenueMode === segmentMode ? "手術売上" : segmentMode;
                            return (
                              <button
                                key={seg.key}
                                type="button"
                                className={`${segmentClass} cursor-pointer border-0 p-0 hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white`}
                                style={segmentStyle}
                                title={segmentTitle}
                                aria-label={`${segmentTitle}。${nextMode === "手術売上" ? "手術売上に戻す" : `${nextMode}に切り替える`}`}
                                onClick={() => onRevenueModeChange(nextMode)}
                              >
                                {segmentContent}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="text-right tabular-nums font-bold text-xs text-slate-800 whitespace-nowrap"
                      title={`${amountHeading}: ${chartAmount(rowTotal, rowTotal)}`}>
                      {chartAmount(rowTotal, rowTotal)}
                    </div>
                    {showCount && (
                      <div className="text-right tabular-nums font-semibold text-xs text-slate-700 whitespace-nowrap"
                        title={`手術会計件数 (月平均): ${caseCount(r.n, countMonths ?? 1)}件`}>
                        {caseCount(r.n, countMonths ?? 1)}件
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── ②' 手術別 件数の月次推移 (2026-09-18 ユーザ指示) ────────────────────────
//   行 = ②TOP20 と同じ術式 (Kコード + 名称)、列 = 年月、値 = 手術会計件数。
//   入院実績の集計表 (MetricMatrix) と同じ流儀で、右端に「実績(月平均)」を置きサイドバーの
//   開始月〜終了月に連動させる。列は⓪月次推移グラフと同じ直近12ヶ月を常に描き
//   (選択期間が12ヶ月より長ければ期間全体)、選択中の期間の月だけ見出しを赤字 + 背景で示す。
//   ★件数は「まるめ」RP (短期滞在手術等基本料3 等・金額0) を含む実勢件数 (2026-09-18 ユーザ裁定)。
//     → ③手術別の金額分布の「手術会計件数」(金額0を除外) とは一致しない。脚注で明示している。
//   ★月平均 = 選択期間のうち「数えられる月」だけの平均 (入院実績 MetricMatrix の rowMeans と同じ流儀)。
//     数えられない月 = ①まだ取り込まれていない月 ②そのKコードが点数マスタに存在しない月
//     (2026改定の採番替え。K178-4 → K178-42 等)。どちらも 0 ではなく "—" にし、分母からも外す。
const countMonthLabel = (ym: string, idx: number) => {
  const mi = Number(ym.slice(4, 6));
  return idx === 0 || mi === 1 || mi === 4 ? `${ym.slice(2, 4)}/${mi}月` : `${mi}月`;
};
// ★2026年度診療報酬改定の施行月。build_surgery_rp.R が点数マスタを pick("20240601") /
//   pick("20260601") の2窓で引いている (点数2024 / 点数2026) ので、この2窓の有無が
//   「その K コードがその月に存在したか」を表す。改定では番号ごと付け替わるコードがあり
//   (K178-4 経皮的脳血栓回収術 → K178-42・K5612ﾛ → K5612ﾊ・点数は同一)、
//   窓外の月を 0 と表示すると「実施していない」と誤読される。
//   実データで例外 0 件を確認済み (2026窓の無いコードが 202606 以降に実績を持つ / その逆 = ともに 0)。
const REVISION_YM = "202606";
const inMasterWindow = (r: SurgeryRpRow, ym: string) => {
  const has24 = r.ten2024 != null, has26 = r.ten2026 != null;
  // どちらの窓にも無い = マスタから引けず名称を fallback したコード。窓で判定できないので数える。
  if (!has24 && !has26) return true;
  return ym >= REVISION_YM ? has26 : has24;
};

// 選択期間の月数 ("YYYY-MM-DD" の月初どうしで数える。注記用)
const monthSpan = (from: string, to: string) => {
  const [y1, m1] = from.slice(0, 7).split("-").map(Number);
  const [y2, m2] = to.slice(0, 7).split("-").map(Number);
  return Math.max(1, (y2 - y1) * 12 + (m2 - m1) + 1);
};

function ProcedureCountMatrix({
  rows, counts, monthsWithData, months, selFrom, selTo, selMonths, selectedKeys, onSelect, isLoading, subtitle, csvName,
}: {
  rows: SurgeryRpRow[];                      // 表示する術式 (②TOP20 と同じ集合・同じ順序)
  counts: SurgeryRpProcedureMonthlyRow[] | undefined;  // ym × Kコード の件数 (直近12ヶ月)。
                                             //   undefined = server が monthlyProcedure を返していない (server が古い)
  monthsWithData: Set<string>;               // その月に手術RPが1件でもある ym (未取込の月と「0件」を分ける)
  months: string[];                          // "YYYYMM" 昇順 (⓪グラフと同じ列)
  selFrom: string; selTo: string;            // 選択期間 "YYYYMM"
  selMonths: number;                         // 選択期間の月数 (= 月平均の分母)
  selectedKeys: Set<string>;
  onSelect: (key: string, additive: boolean) => void;
  isLoading: boolean;
  subtitle: string;
  csvName: string;
}) {
  const countMap = useMemo(() => {
    const m = new Map<string, Map<string, number>>();
    for (const c of counts ?? []) {
      if (!m.has(c.kcode)) m.set(c.kcode, new Map());
      m.get(c.kcode)!.set(c.ym, c.n);
    }
    return m;
  }, [counts]);
  // ★セルは「0件」と「数えられない月」を分ける (2026-09-18 敵対レビュー指摘 + ユーザ裁定)。
  //   null ("—") になるのは3つ: ①server が monthlyProcedure を返していない (旧 server)
  //   ②その月がまだ取り込まれていない (病院全体で手術RPが1行も無い)
  //   ③その K コードが当月の点数マスタに存在しない (改定の採番替え)。
  //   それ以外で行が無ければ、その術式が本当に 0 件。
  const inSel = (ym: string) => ym >= selFrom && ym <= selTo;
  const selMonthList = months.filter(inSel);
  // ★`useSort` は列ヘッダをクリックした時点の `get` 関数を ref に保持し続ける (useSort.tsx の getRef)。
  //   `cell`/`meanOf` がその render の countMap を閉じ込めると、期間・科・予定緊急・術式検索を変えた後も
  //   **古いデータで並び替えたまま、ヘッダだけ「降順」を表示する**（実機再現: 6月降順 31,26,21,15,14 で
  //   科フォーカスへ切替 → 21,12,13,7,9 と降順が崩れるのに aria-sort は descending のまま。
  //   2026-09-18 敵対レビュー adv-ui の指摘）。ref 越しに読むことで、保持された古いクロージャも最新を見る。
  const latest = useRef({ counts, countMap, monthsWithData, selMonthList });
  latest.current = { counts, countMap, monthsWithData, selMonthList };
  const cell = (r: SurgeryRpRow, ym: string): number | null => {
    const cur = latest.current;
    if (cur.counts === undefined) return null;
    if (!cur.monthsWithData.has(ym)) return null;
    if (!inMasterWindow(r, ym)) return null;
    return cur.countMap.get(r.kcode)?.get(ym) ?? 0;
  };
  // ★月平均は行ごとに分母が変わる (採番替えのコードは有効窓の月だけで平均する)。
  //   分子も counts から取る = 列に見えている数字の合計であって、③の「手術会計件数」ではない
  //   (③は金額0のまるめRPを除くので一致しない。ユーザ裁定 2026-09-18)。
  const meanOf = (r: SurgeryRpRow): { mean: number | null; denom: number } => {
    let sum = 0, d = 0;
    for (const ym of latest.current.selMonthList) {
      const v = cell(r, ym);
      if (v == null) continue;
      sum += v; d++;
    }
    return { mean: d > 0 ? sum / d : null, denom: d };
  };
  // 注記用: 選択期間のうち「数えられる月」の数 (行によって違う場合は最大値を代表に使う)
  const denomMax = Math.max(0, ...rows.map((r) => meanOf(r).denom));
  const s = useSort(rows);

  // ★一番上に置く合計行 (2026-09-18 ユーザ指示)。
  //   **表示中の20術式の部分和ではなく、いまの絞り込み条件 (期間・診療科・予定緊急・術式検索) での
  //   全術式の合計**＝この表の母集団そのもの。TOP20 の和だと「分母」にならず、
  //   科を切り替えたときに何件中の話なのかが読めない。
  //   ※⓪月次推移・診療科別の件数とは母集団が違う (あちらは主たるKコードの無い手術関連RPも数える)。
  const totalByYm = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of counts ?? []) m.set(c.ym, (m.get(c.ym) ?? 0) + c.n);
    return m;
  }, [counts]);
  const kcodeCount = useMemo(() => new Set((counts ?? []).map((c) => c.kcode)).size, [counts]);
  // 合計行のセル: 月ごとの「数えられない」判定は行 (Kコード) に依らないので、未取込の月だけが "—"。
  const totalCell = (ym: string): number | null => {
    if (counts === undefined) return null;
    if (!monthsWithData.has(ym)) return null;
    return totalByYm.get(ym) ?? 0;
  };
  const totalMean = (() => {
    let sum = 0, d = 0;
    for (const ym of selMonthList) {
      const v = totalCell(ym);
      if (v == null) continue;
      sum += v; d++;
    }
    return { mean: d > 0 ? sum / d : null, denom: d };
  })();

  const csvRows = useMemo(() => {
    const head: Record<string, string | number> = {
      Kコード: "＝合計＝", 名称: `全術式 計 (${kcodeCount} Kコード)`, R6点数: "", R8点数: "",
    };
    months.forEach((ym) => { head[`${ym.slice(0, 4)}-${ym.slice(4, 6)}`] = totalCell(ym) ?? ""; });
    head["実績(月平均)"] = totalMean.mean != null ? Number(totalMean.mean.toFixed(2)) : "";
    head["月平均の分母(数えられる月数)"] = totalMean.denom;
    head["選択期間の月数"] = selMonths;
    return [head, ...rows.map((r) => {
    const row: Record<string, string | number> = {
      Kコード: r.kcode, 名称: r.kname,
      R6点数: r.ten2024 ?? "", R8点数: r.ten2026 ?? "",
    };
    months.forEach((ym) => { row[`${ym.slice(0, 4)}-${ym.slice(4, 6)}`] = cell(r, ym) ?? ""; });
    const t = meanOf(r);
    row["実績(月平均)"] = t.mean != null ? Number(t.mean.toFixed(2)) : "";
    row["月平均の分母(数えられる月数)"] = t.denom;
    row["選択期間の月数"] = selMonths;
    return row;
    })];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, months, countMap, monthsWithData, selFrom, selTo, selMonths, totalByYm, kcodeCount]);

  return (
    <>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <h3 className="text-sm font-bold">📋 手術別 件数の月次推移</h3>
        <span className="text-[11px] font-normal text-slate-500">{subtitle}</span>
        <span className="text-[10px] text-red-600 font-semibold">赤字の年月 = 選択中の期間</span>
        {isLoading && <span className="text-[10px] text-slate-400 no-print">loading…</span>}
        <CsvButton rows={csvRows as unknown as Record<string, unknown>[]} filename={csvName} style={{ marginLeft: "auto" }} />
      </div>
      {denomMax !== selMonths && denomMax > 0 && (
        <p className="text-[10px] text-amber-700 mb-1.5">
          選択 {selMonths}ヶ月のうちデータのある {denomMax}ヶ月だけで平均している。
        </p>
      )}
      {counts === undefined && !isLoading && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
          件数の月次データ (monthlyProcedure) が API から返っていません。server が古い可能性があります
          (全て「—」表示。0件ではありません)。
        </div>
      )}
      <div className="opesr-scroll overflow-auto" style={{ maxHeight: "60vh" }}>
        <table className="opesr-table text-[11px] border-collapse w-full">
          <thead className="sticky top-0 bg-slate-100 z-[1]">
            <tr>
              <SortTh s={s} sortKey="kcode" get={(r) => (r as SurgeryRpRow).kcode}
                className="px-1.5 py-1 border-b text-left whitespace-nowrap sticky left-0 bg-slate-100 z-[2]">Kコード</SortTh>
              <SortTh s={s} sortKey="kname" get={(r) => (r as SurgeryRpRow).kname}
                className="px-1.5 py-1 border-b text-left">名称</SortTh>
              {/* ★R6/R8 の点数 = この表の「—」の理由でもある (当該年度のマスタに無いコードは
                  その期間を "—" にしている)。空欄 ⇔ "—" の対応が読み取れるよう名称の直後に置く。 */}
              <SortTh s={s} sortKey="ten2024" get={(r) => (r as SurgeryRpRow).ten2024}
                className="px-1.5 py-1 border-b text-right whitespace-nowrap"
                title="令和6年度(2024年度)改定の一般点数 (点)。空欄 = 当時のマスタに当コードなし">R6点数</SortTh>
              <SortTh s={s} sortKey="ten2026" get={(r) => (r as SurgeryRpRow).ten2026}
                className="px-1.5 py-1 border-b text-right whitespace-nowrap"
                title="令和8年度(2026年度)改定の一般点数 (点)。空欄 = 現行マスタに当コードなし">R8点数</SortTh>
              {months.map((ym, i) => (
                <SortTh key={ym} s={s} sortKey={`cnt:${ym}`} get={(r) => cell(r as SurgeryRpRow, ym)}
                  className={`px-1.5 py-1 border-b text-right tabular-nums whitespace-nowrap ${
                    inSel(ym) ? "text-red-600 font-bold bg-amber-50" : ""}`}>
                  {countMonthLabel(ym, i)}
                </SortTh>
              ))}
              <SortTh s={s} sortKey="mean" get={(r) => meanOf(r as SurgeryRpRow).mean}
                className="px-1.5 py-1 border-b text-right whitespace-nowrap bg-slate-200"
                title="選択期間 (開始月〜終了月) のうち数えられる月だけの平均件数">実績(月平均)</SortTh>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !isLoading && (
              <tr><td colSpan={months.length + 5} className="text-center text-slate-400 py-4">該当データなし</td></tr>
            )}
            {/* ★合計行は常に最上段 (ソートしても動かさない)。表示中の20術式の和ではなく
                いまの絞り込み条件での全術式合計＝この表の母集団。 */}
            {rows.length > 0 && (
              <tr className="border-b-2 border-slate-300 bg-slate-100 font-bold">
                <td className="px-1.5 py-1 font-mono whitespace-nowrap sticky left-0 z-[1] bg-slate-100">計</td>
                <td className="px-1.5 py-1 leading-tight">
                  全術式 計
                  <span className="ml-1 text-[9px] font-normal text-slate-500 whitespace-nowrap">
                    （{kcodeCount.toLocaleString()} Kコード・下の{rows.length}行はこの内訳の一部）
                  </span>
                </td>
                <td className="px-1.5 py-1 text-right text-slate-300 font-normal">—</td>
                <td className="px-1.5 py-1 text-right text-slate-300 font-normal">—</td>
                {months.map((ym) => {
                  const v = totalCell(ym);
                  const ymText = `${ym.slice(0, 4)}年${Number(ym.slice(4, 6))}月`;
                  return (
                    <td key={ym}
                      className={`px-1.5 py-1 text-right tabular-nums ${inSel(ym) ? "bg-amber-100" : ""} ${
                        v == null ? "text-slate-400 font-normal" : ""}`}
                      title={v == null
                        ? `${ymText}: この月の手術データがまだ入っていない → 0件ではない`
                        : `${ymText} 全術式: ${v.toLocaleString()}件`}>
                      {v == null ? "—" : v.toLocaleString()}
                    </td>
                  );
                })}
                <td className="px-1.5 py-1 text-right tabular-nums bg-slate-200"
                  title={totalMean.mean == null
                    ? "選択期間に数えられる月が無い"
                    : `選択期間の全術式の件数 ${Math.round(totalMean.mean * totalMean.denom).toLocaleString()}件 ÷ ${totalMean.denom}ヶ月`}>
                  {totalMean.mean == null ? "—" : totalMean.mean.toFixed(totalMean.denom === 1 ? 0 : 1)}
                </td>
              </tr>
            )}
            {s.rows.map((r) => {
              const selected = selectedKeys.has(r.kcode);
              return (
                <tr key={r.kcode}
                  className={`group border-b border-slate-100 cursor-pointer ${
                    selected ? "bg-amber-100 outline outline-2 outline-amber-300 font-semibold" : "hover:bg-blue-50"
                  }`}
                  onClick={(e) => onSelect(r.kcode, e.ctrlKey || e.metaKey)}
                  title="通常クリックで1術式、Ctrl/⌘+クリックで複数選択"
                  role="button"
                  tabIndex={0}
                  aria-selected={selected}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    onSelect(r.kcode, e.ctrlKey || e.metaKey);
                  }}>
                  {/* ★不透明な背景を個別に指定した列は行の hover が効かない (2026-09-18 adv-ui 指摘)。
                      横スクロール時に行を追う唯一の目印なので group-hover で行と一緒に色を変える。 */}
                  <td className={`px-1.5 py-1 font-mono whitespace-nowrap sticky left-0 z-[1] ${
                    selected ? "bg-amber-100" : "bg-white group-hover:bg-blue-50"}`}>
                    {r.kcode}
                  </td>
                  <td className="px-1.5 py-1 leading-tight">
                    {selected && "✓ "}{r.kname}
                    {r.tencd_count > 1 && <span className="ml-1 text-[9px] text-slate-400 whitespace-nowrap">（{r.tencd_count}診療行為を集約）</span>}
                  </td>
                  <td className="px-1.5 py-1 text-right tabular-nums text-slate-600"
                    title={r.ten2024 == null ? "令和6年度のマスタに当コードなし" : `令和6年度 ${i0(r.ten2024)}点`}>
                    {i0(r.ten2024)}
                  </td>
                  <td className="px-1.5 py-1 text-right tabular-nums text-slate-600"
                    title={r.ten2026 == null ? "令和8年度のマスタに当コードなし" : `令和8年度 ${i0(r.ten2026)}点`}>
                    {i0(r.ten2026)}
                  </td>
                  {months.map((ym) => {
                    const v = cell(r, ym);
                    const ymText = `${ym.slice(0, 4)}年${Number(ym.slice(4, 6))}月`;
                    const why = counts === undefined
                      ? "件数データが API から返っていない"
                      : !monthsWithData.has(ym)
                        ? "この月の手術データがまだ入っていない"
                        : `${r.kcode} は当月の点数マスタに無い (改定の採番替え)`;
                    return (
                      <td key={ym}
                        className={`px-1.5 py-1 text-right tabular-nums ${
                          inSel(ym) ? (selected ? "bg-amber-50" : "bg-amber-50 group-hover:bg-blue-50") : ""} ${
                          v === 0 ? "text-slate-300" : v == null ? "text-slate-400" : ""}`}
                        title={v == null
                          ? `${ymText}: ${why} → 0件ではない`
                          : `${ymText} ${r.kname}: ${v}件`}>
                        {v == null ? "—" : v.toLocaleString()}
                      </td>
                    );
                  })}
                  {(() => {
                    const t = meanOf(r);
                    return (
                      <td className={`px-1.5 py-1 text-right tabular-nums font-semibold ${
                          selected ? "bg-amber-100" : "bg-slate-50 group-hover:bg-blue-50"}`}
                        title={t.mean == null
                          ? "選択期間に数えられる月が無い"
                          : t.denom === 1
                            ? `選択中の1ヶ月の件数 ${(t.mean).toLocaleString()}件`
                            : `選択期間の件数 ${Math.round(t.mean * t.denom).toLocaleString()}件 ÷ ${t.denom}ヶ月`}>
                        {t.mean == null ? "—" : t.mean.toFixed(t.denom === 1 ? 0 : 1)}
                      </td>
                    );
                  })()}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function SurgeryRpPanel({ metrics = [], builtAt = "" }: {
  metrics?: MetricRow[];   // ContextBar (印刷ヘッダ) 用。手術売上の分岐では参照しないが I/F を揃える
  builtAt?: string;
} = {}) {
  const f = useFilters();
  // 既定期間 = 最新月の1ヶ月 (2026-07-21 ユーザ指示。年度累計等はサイドバーで期間変更)
  const dateTo = f.opeTo ?? f.asOfMonth;
  const dateFrom = f.opeFrom ?? f.asOfMonth;
  const dept = f.focusedDept;
  const yotei = f.yotei === "合計" ? null : f.yotei;
  const revenueMode = f.opeRevenueMode;
  const handOnly = revenueMode === "手技売上";
  const materialOnly = revenueMode === "材料薬剤費";
  const rowRevenueValue = (r: SurgeryRpRow) =>
    handOnly
      ? r.gi_sum
      : materialOnly
        ? Number(r.zai_sum ?? 0) + Number(r.yaku_sum ?? 0)
        : r.total_sum;
  const summaryRevenueValue = (r: SurgeryRpSummaryRow) =>
    handOnly
      ? r.gi_en
      : materialOnly
        ? r.zai_en + r.yaku_en
        : r.total_en;
  const chartMetricTitle = handOnly
    ? "手技売上"
    : materialOnly
      ? "材料薬剤費"
      : "手術売上構成";
  const sortAmountLabel = handOnly
    ? "手技金額"
    : materialOnly
      ? "材料薬剤費"
      : "手術総金額";

  // ⓪月次推移の描画範囲 = 直近12ヶ月。選択期間が12ヶ月より長いときは期間の全体が入るまで遡る。
  // ★[2026-08-21 ユーザ裁定] 右端は **選択終了月** で確定 (最新月固定にはしない)。
  //   つまり期間を過去へ動かすと最新月は図から外れる。これは仕様であって不具合ではない
  //   ("選択期間が必ず図に入る" 方を優先した)。直そうとしないこと。
  const monthlyFrom = useMemo(() => {
    const back12 = monthShift(dateTo, -11);
    return back12 < dateFrom ? back12 : dateFrom;
  }, [dateFrom, dateTo]);

  const { data, isLoading, error } = useQuery<SurgeryRpResponse>({
    queryKey: ["surgery_rp", dateFrom, dateTo, monthlyFrom, dept, f.granularity, yotei, f.opeProcedureQuery],
    queryFn: () => api.surgeryRp({
      dateFrom,
      dateTo,
      monthlyFrom,
      dept,
      granularity: f.granularity,
      yotei,
      procedureQuery: f.opeProcedureQuery || null,
    }),
    placeholderData: (p) => p,
  });
  const rows: SurgeryRpRow[] = data?.rows ?? [];
  const summary: SurgeryRpSummaryRow[] = data?.summary ?? [];
  const [selectedDeptKeys, setSelectedDeptKeys] = useState<Set<string>>(new Set());
  const [selectedProcedureKeys, setSelectedProcedureKeys] = useState<Set<string>>(new Set());
  const [procedureAmountBasis, setProcedureAmountBasis] = useState<ProcedureAmountBasis>("total");
  const s = useSort(rows, {
    defaultSort: {
      key: handOnly ? "gisum" : materialOnly ? "zysum" : "totalsum",
      dir: "desc",
      get: rowRevenueValue,
    },
    resetKey: revenueMode,
  });
  const total = summary.reduce((a, r) => ({
    n: a.n + r.n, total_en: a.total_en + r.total_en, gi_en: a.gi_en + r.gi_en,
    zai_en: a.zai_en + r.zai_en, yaku_en: a.yaku_en + r.yaku_en,
  }), { n: 0, total_en: 0, gi_en: 0, zai_en: 0, yaku_en: 0 });
  // ★売上は「期間の月平均」で出す (2026-09-18 ユーザ指示)。
  //   入院実績・行為別売上と同じ流儀＝期間を広げても金額の桁が変わらず、月どうしを比べられる。
  //   分母は**データのある月の数**。未取込の月 (病院全体で手術RPが1行も無い月) を分母に入れると
  //   月平均が過小に出るため、件数マトリクスと同じ考え方で外す。
  const periodMonths = useMemo(() => {
    const from = dateFrom.slice(0, 7).replace("-", "");
    const to = dateTo.slice(0, 7).replace("-", "");
    const n = (data?.monthsWithData ?? []).filter((ym) => ym >= from && ym <= to).length;
    return Math.max(1, n || monthSpan(dateFrom, dateTo));
  }, [data, dateFrom, dateTo]);

  const hospitalKey = "__hospital__";
  // ★金額も件数も月平均 (2026-09-18 ユーザ指示。当初は件数だけ期間実数にしていたが、
  //   同じ棒の中で「金額は月平均・件数は期間合計」が混在すると読み違えるため揃えた)
  const perMonth = (r: { n: number; total_en: number; gi_en: number; zai_en: number; yaku_en: number }) => ({
    n: r.n / periodMonths,
    total_en: r.total_en / periodMonths,
    gi_en: r.gi_en / periodMonths,
    zai_en: r.zai_en / periodMonths,
    yaku_en: r.yaku_en / periodMonths,
  });
  const allDeptChartRows: RevenueChartRow[] = [
    { key: hospitalKey, label: "病院全体", isHospital: true, ...perMonth(total) },
    ...[...summary]
      .sort((a, b) => summaryRevenueValue(b) - summaryRevenueValue(a))
      .map((r) => ({ key: r.dept, label: r.dept, ...perMonth(r) })),
  ];
  // API の科別 summary はフォーカスに関係なく全科を返す。
  // サイドバー/通常クリックの科フォーカスを最優先し、Ctrl/⌘ 選択時だけ複数科を比較する。
  const deptChartRows = dept
    ? allDeptChartRows.filter((r) => r.key === dept)
    : selectedDeptKeys.size > 0
      ? allDeptChartRows.filter((r) => selectedDeptKeys.has(r.key))
      : allDeptChartRows;
  const deptChartSelected = dept ? new Set([dept]) : selectedDeptKeys;

  // ⓪月次推移: API は ym × 科で返すので、いま見ているスコープ (科フォーカス / Ctrl 複数科 / 病院全体)
  //   に合わせてクライアントで合算する。空 Set = 全科 (= 病院全体)。
  const monthlyDeptKeys = useMemo(() => {
    if (dept) return new Set([dept]);
    // 「病院全体」を Ctrl 選択に含めた場合は全科と同義なので絞り込まない。
    if (selectedDeptKeys.size === 0 || selectedDeptKeys.has(hospitalKey)) return new Set<string>();
    return selectedDeptKeys;
  }, [dept, selectedDeptKeys]);
  const monthlyScopeLabel = (() => {
    if (selectedProcedureKeys.size > 0) {
      const [first] = [...selectedProcedureKeys];
      const kname = rows.find((r) => r.kcode === first)?.kname ?? first;
      return selectedProcedureKeys.size === 1
        ? `${first} ${kname}`
        : `${first} 他${selectedProcedureKeys.size - 1}術式`;
    }
    if (dept) return dept;
    if (monthlyDeptKeys.size === 0) return "病院全体";
    const [first] = [...monthlyDeptKeys];
    return monthlyDeptKeys.size === 1 ? first : `${first} 他${monthlyDeptKeys.size - 1}科`;
  })();
  const monthlyChartRows: RevenueChartRow[] = useMemo(() => {
    const acc = new Map<string, RevenueChartRow>();
    const blank = (ym: string): RevenueChartRow =>
      ({ key: ym, label: ymLabel(ym), n: 0, total_en: 0, gi_en: 0, zai_en: 0, yaku_en: 0 });
    // ★術式を選んでいるときは、その術式だけの月次にする (2026-09-18 ユーザ指摘)。
    //   選択術式はクライアント側の state なのでサーバでは絞れない。ym × kcode で返している
    //   monthlyProcedure から選択分だけ合算する (診療科の絞り込みは既にサーバ側で効いている)。
    //   ※monthlyProcedure は「主たるKコードのある手術RP」に限るので、術式を選ばないときの
    //     全体 (monthly = 主たるKコードなしも含む) とは母集団が違う。だから既定は monthly のまま。
    if (selectedProcedureKeys.size > 0) {
      for (const m of data?.monthlyProcedure ?? []) {
        if (!selectedProcedureKeys.has(m.kcode)) continue;
        const cur = acc.get(m.ym) ?? blank(m.ym);
        cur.n = (cur.n ?? 0) + m.n;
        cur.total_en += m.total_en;
        cur.gi_en += m.gi_en;
        cur.zai_en += m.zai_en;
        cur.yaku_en += m.yaku_en;
        acc.set(m.ym, cur);
      }
    } else {
      for (const m of data?.monthly ?? []) {
        if (monthlyDeptKeys.size > 0 && !monthlyDeptKeys.has(m.dept)) continue;
        const cur = acc.get(m.ym) ?? blank(m.ym);
        cur.n = (cur.n ?? 0) + m.n;
        cur.total_en += m.total_en;
        cur.gi_en += m.gi_en;
        cur.zai_en += m.zai_en;
        cur.yaku_en += m.yaku_en;
        acc.set(m.ym, cur);
      }
    }
    // 実績が無い月も 0 で埋めて x 軸を連続させる (欠測月で棒が詰まらないように)。
    // 日の桁は無視して月初どうしで回す (期間指定が月初以外でも最終月を落とさない)。
    const out: RevenueChartRow[] = [];
    const endYm = dateTo.slice(0, 7).replace("-", "");
    for (let d = `${monthlyFrom.slice(0, 7)}-01`; ; d = monthShift(d, 1)) {
      const ym = d.slice(0, 7).replace("-", "");
      if (ym > endYm) break;
      out.push(acc.get(ym) ?? { key: ym, label: ymLabel(ym), n: 0, total_en: 0, gi_en: 0, zai_en: 0, yaku_en: 0 });
    }
    return out;
  }, [data, monthlyDeptKeys, monthlyFrom, dateTo, selectedProcedureKeys]);

  // ②' 件数マトリクスで「0件」と「その月のデータがまだ無い」を分けるための集合。
  //   server が monthlyProcedure と同じ母集団 (まるめRP込み) で返す ym の一覧。
  const monthsWithData = useMemo(
    () => new Set(data?.monthsWithData ?? []),
    [data],
  );

  // 1件あたり = 期間合計 ÷ 件数 (月数では割らない) / 月平均 = 期間合計 ÷ 月数
  const procedureDivisor = (r: SurgeryRpRow) =>
    procedureAmountBasis === "perCase" ? Math.max(1, r.n) : periodMonths;
  const procedureSortValue = (r: SurgeryRpRow) =>
    rowRevenueValue(r) / procedureDivisor(r);
  const selectedProcedureRows = rows
    .filter((r) => selectedProcedureKeys.has(r.kcode))
    .sort((a, b) => procedureSortValue(b) - procedureSortValue(a));
  const topProcedureRows = [...rows]
    .sort((a, b) => procedureSortValue(b) - procedureSortValue(a))
    .slice(0, TOP_N);
  // ②TOP20 の横棒と ②' 件数マトリクスは同じ術式集合を見る (グラフと表を1対1に対応させる)。
  const shownProcedureRows = selectedProcedureRows.length > 0 ? selectedProcedureRows : topProcedureRows;
  const procedureChartRows: RevenueChartRow[] = shownProcedureRows.map((r) => {
    const divisor = procedureDivisor(r);
    return {
      key: r.kcode,
      label: `${r.kcode} ${r.kname}${r.tencd_count > 1 ? `（${r.tencd_count}診療行為）` : ""}`,
      n: r.n / periodMonths,
      total_en: r.total_sum / divisor,
      gi_en: r.gi_sum / divisor,
      zai_en: Number(r.zai_sum ?? 0) / divisor,
      yaku_en: Number(r.yaku_sum ?? 0) / divisor,
    };
  });

  // 期間・予定緊急・粒度が変わったら、異なる母集団の選択を持ち越さない。
  useEffect(() => {
    setSelectedDeptKeys(new Set());
    setSelectedProcedureKeys(new Set());
  }, [dateFrom, dateTo, f.granularity, yotei, f.opeProcedureQuery]);
  // 科フォーカス時は科別比較選択を解除。フォーカスが変わると術式の母集団も変わる。
  useEffect(() => {
    if (dept) setSelectedDeptKeys(new Set());
    setSelectedProcedureKeys(new Set());
  }, [dept]);

  const selectDept = (key: string, additive: boolean) => {
    if (!additive) {
      setSelectedDeptKeys(new Set());
      if (key === hospitalKey) f.setFocusedDept(null);
      else f.setFocusedDept(dept === key ? null : key);
      return;
    }
    setSelectedDeptKeys((prev) => {
      const next = new Set(prev);
      // 1科フォーカスから複数比較へ移る場合は、現在科を比較対象に引き継ぐ。
      if (dept) next.add(dept);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    if (dept) f.setFocusedDept(null);
  };

  const selectProcedure = (key: string, additive: boolean) => {
    setSelectedProcedureKeys((prev) => {
      if (!additive) {
        // 同じ1術式を再クリックしたら選択解除し、既定の上位 TOP_N 件へ戻す。
        return prev.size === 1 && prev.has(key) ? new Set() : new Set([key]);
      }
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const ymL = (d: string) => `${d.slice(2, 4)}年${d.slice(5, 7)}月`;

  const downloadCsv = () => {
    const head = ["Kコード", "名称", "配下診療行為数", "R6点数", "R8点数",
      "手技金額/件_中央値", "手技金額/件_Q1", "手技金額/件_Q3",
      "材料金額/件_中央値", "材料金額/件_Q1", "材料金額/件_Q3",
      "手術会計件数(月平均)", "手術会計件数(期間合計)", "手技金額(月平均)", "総額(月平均)",
      "手技金額(期間合計)", "総額(期間合計)", "月数", "配下レセ電コード"];
    const lines = [head.join(",")];
    for (const r of s.rows) {
      lines.push([r.kcode, `"${r.kname}"`, r.tencd_count, r.ten2024 ?? "", r.ten2026 ?? "",
        Math.round(r.gi_med), Math.round(r.gi_q1), Math.round(r.gi_q3),
        Math.round(r.zai_med), Math.round(r.zai_q1), Math.round(r.zai_q3),
        (r.n / periodMonths).toFixed(2), r.n,
        Math.round(r.gi_sum / periodMonths), Math.round(r.total_sum / periodMonths),
        Math.round(r.gi_sum), Math.round(r.total_sum), periodMonths, r.tencd].join(","));
    }
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `${revenueMode}_${dateFrom.slice(0, 7)}_${dateTo.slice(0, 7)}${dept ? `_${dept}` : ""}${yotei ? `_${yotei}` : ""}.csv`;
    a.click();
  };

  const header = (
    <div className="flex items-center gap-2 mb-2 flex-wrap">
      <h2 className="text-sm font-bold">🔪 {revenueMode}</h2>
      <span className="text-xs px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700">
        {ymL(dateFrom)}〜{ymL(dateTo)}
      </span>
      {yotei && <span className="text-xs px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">{yotei}のみ</span>}
      {f.opeProcedureQuery && (
        <button
          onClick={() => f.setOpeProcedureQuery("")}
          className="text-xs px-2 py-0.5 rounded bg-sky-50 border border-sky-200 text-sky-800 hover:bg-sky-100 no-print"
          title="術式の絞り込みを解除"
        >
          🔎 {f.opeProcedureQuery} ✕
        </button>
      )}
      {f.opeProcedureQuery && (
        <span className="hidden print:inline text-xs px-2 py-0.5 rounded bg-sky-50 border border-sky-200 text-sky-800">
          🔎 術式: {f.opeProcedureQuery}
        </span>
      )}
      {dept && (
        <button onClick={() => f.setFocusedDept(null)}
          className="text-xs px-2 py-0.5 rounded bg-amber-100 border border-amber-200 text-amber-800 hover:bg-amber-200 no-print"
          title="科フォーカスを解除">🎯 {dept} ✕</button>
      )}
      {dept && <span className="hidden print:inline text-xs px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">🎯 {dept}</span>}
      {isLoading && <span className="text-[10px] text-slate-400 no-print">loading…</span>}
      <span className="ml-auto text-[10px] text-slate-400 cursor-help"
        title="手術 = 手術+手術材料+手術薬剤 (輸血・麻酔は除外) = 行為別売上の「手術」区分と一致。手術別はKコード単位で、同じKコード配下のレセ電コードを合算。各RPの主たるKコード = 手術RP内で最大点数の手技Kコード。主たるKコードなしの手術関連RPは診療科別グラフのみに計上。診療科 = 入院主科 (手術日の在院科・行為別売上と同じ振り分け)。金額 = 会計明細円 (労災も円のまま合算)。短期滞在手術等のまるめ (金額0のRP) は除外。マスタ点数 = 一般の点数 (2024年度/2026年度)">ⓘ</span>
    </div>
  );

  return (
    <div>
      {error && <div className="text-[11px] text-red-700 mb-1">取得エラー: {(error as Error).message}</div>}

      <div className={dept ? "opesr-print-combined" : undefined}>
        {/* ① 診療科別 手術売上構成 (通常積み上げ ⇄ 100%積み上げ) */}
        <section className="opesr-chart-sec card p-3 mb-3">
          {header}
          <RevenueStackChart
            rows={deptChartRows}
            title={`診療科別 ${chartMetricTitle}（月平均）`}
            description={revenueMode === "手術売上"
              ? `金額は選択期間 ${periodMonths}ヶ月の月平均。通常クリック＝1科フォーカス、Ctrl/⌘＋クリック＝複数科比較。棒の手技・材料薬剤をクリックして表示を切替。`
              : `金額は選択期間 ${periodMonths}ヶ月の月平均。${sortAmountLabel}の降順。通常クリック＝1科フォーカス、Ctrl/⌘＋クリック＝複数科比較。`}
            selectedKeys={deptChartSelected}
            onSelect={selectDept}
            revenueMode={revenueMode}
            onRevenueModeChange={f.setOpeRevenueMode}
          />
        </section>

        {/* ② 手術別 TOP_N / 選択術式の積み上げ横棒 */}
        <section className="opesr-procedure-chart-sec card p-3 mb-3">
          <RevenueStackChart
            rows={procedureChartRows}
            title={selectedProcedureRows.length > 0
              ? `選択中の手術（${selectedProcedureRows.length}術式・${procedureAmountBasis === "perCase" ? "1件あたり" : "月平均"}）`
              : f.opeProcedureQuery
                ? `手術別 ${chartMetricTitle} 検索結果${rows.length > TOP_N ? ` TOP${TOP_N} / ${rows.length}Kコード` : `（${rows.length}Kコード）`}（${procedureAmountBasis === "perCase" ? "1件あたり" : "月平均"}）`
              : `手術別 ${chartMetricTitle} TOP${TOP_N}（${procedureAmountBasis === "perCase" ? "1件あたり" : "月平均"}）`}
            description={f.opeProcedureQuery
              ? `サイドバーの術式検索に一致する結果を、${procedureAmountBasis === "perCase" ? "1件あたり平均" : "月平均"}の${sortAmountLabel}順で表示。`
              : `${procedureAmountBasis === "perCase" ? "1件あたり平均" : "月平均"}の${sortAmountLabel}順。下表の通常クリックで1術式、Ctrl/⌘＋クリックで複数術式を選択。`}
            selectedKeys={selectedProcedureKeys}
            onSelect={selectProcedure}
            revenueMode={revenueMode}
            onRevenueModeChange={f.setOpeRevenueMode}
            valueBasis={procedureAmountBasis}
            onValueBasisChange={setProcedureAmountBasis}
            countMonths={periodMonths}
            wideLabels
            showCount
          />
        </section>
      </div>

      {/* ③ 手術売上 月次推移 (縦積み上げ棒・直近12ヶ月・選択期間の月ラベルは赤字)
          ★2026-09-18 ユーザ指示で **タブ最上部 (2026-08-21 の配置) から 3 番目へ移動**。
            並びは 診療科別 → 手術別TOP20 → 月次推移 → 件数の推移 → 金額分布。
          ★印刷では独立ページにする。直前が②(横棒20行)で 1 ページを使い切るため、
            指定なしだと②のページに流れ込んで溢れる。ページ単体で条件が分かるよう
            条件ヘッダも載せる (件数マトリクスと同じ流儀)。 */}
      <section className="opesr-monthly-sec card p-3 mb-3">
        <ContextBar forPrint metrics={metrics} builtAt={builtAt} />
        <MonthlyRevenueChart
          rows={monthlyChartRows}
          selFrom={dateFrom.slice(0, 7).replace("-", "")}
          selTo={dateTo.slice(0, 7).replace("-", "")}
          revenueMode={revenueMode}
          onRevenueModeChange={f.setOpeRevenueMode}
          scopeLabel={monthlyScopeLabel}
          isLoading={isLoading}
        />
      </section>

      {/* ④ 手術別 件数の月次推移 (2026-09-18 ユーザ指示・②TOP20 と同じ術式) */}
      <section className="opesr-count-sec card p-3 mb-3">
        {/* ★印刷時、このセクションは独立ページになるので、ページ単体で「いつ・どの条件の数字か」が
            分かるように条件ヘッダを載せる (入院実績 PrototypePanel と同じ流儀・画面では非表示)。 */}
        <ContextBar forPrint metrics={metrics} builtAt={builtAt} />
        <ProcedureCountMatrix
          rows={shownProcedureRows}
          counts={data?.monthlyProcedure}
          monthsWithData={monthsWithData}
          months={monthlyChartRows.map((r) => r.key)}
          selFrom={dateFrom.slice(0, 7).replace("-", "")}
          selTo={dateTo.slice(0, 7).replace("-", "")}
          selMonths={monthSpan(dateFrom, dateTo)}
          selectedKeys={selectedProcedureKeys}
          onSelect={selectProcedure}
          isLoading={isLoading}
          subtitle={`${selectedProcedureRows.length > 0
            ? `選択中の${selectedProcedureRows.length}術式`
            : `上図と同じ ${shownProcedureRows.length} 術式（並びは${chartMetricTitle}の${
                procedureAmountBasis === "perCase" ? "1件あたり" : "合計"}順）`}・直近${monthlyChartRows.length}ヶ月`}
          csvName={`手術別件数_${dateFrom.slice(0, 7)}_${dateTo.slice(0, 7)}${dept ? `_${dept}` : ""}${yotei ? `_${yotei}` : ""}`}
        />
      </section>

      {/* ⑤ 手術別の金額分布 (円・手技円/合計 降順) */}
      <section className="opesr-detail-sec card p-3">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h3 className="text-sm font-bold">手術別の金額分布</h3>
          {data && <span className="perf-pill text-[10px]">{rows.length} Kコード / {Number(data.totalRp).toLocaleString()} 件</span>}
          <span className="text-[10px] text-slate-400 no-print">通常クリック＝1術式、Ctrl/⌘＋クリック＝複数術式を上のグラフへ</span>
          <button onClick={downloadCsv} className="no-print text-xs px-2 py-0.5 rounded bg-emerald-600 text-white hover:bg-emerald-700 ml-auto">CSV</button>
        </div>
        <div className="opesr-scroll overflow-auto" style={{ maxHeight: "68vh" }}>
          <table className="opesr-table text-[11px] border-collapse w-full">
            <thead className="sticky top-0 bg-slate-100 z-[1]">
              <tr>
                <SortTh s={s} sortKey="kcode" get={(r) => (r as SurgeryRpRow).kcode} className="px-1.5 py-1 border-b text-left whitespace-nowrap">Kコード</SortTh>
                <SortTh s={s} sortKey="kname" get={(r) => (r as SurgeryRpRow).kname} className="px-1.5 py-1 border-b text-left">名称</SortTh>
                <SortTh s={s} sortKey="ten2024" get={(r) => (r as SurgeryRpRow).ten2024} className="px-1.5 py-1 border-b text-right whitespace-nowrap" title="令和6年度(2024年度)改定の一般点数 (点)。空欄 = 当時のマスタに当コードなし">R6点数</SortTh>
                <SortTh s={s} sortKey="ten2026" get={(r) => (r as SurgeryRpRow).ten2026} className="px-1.5 py-1 border-b text-right whitespace-nowrap" title="令和8年度(2026年度)改定の一般点数 (点)。空欄 = 現行マスタに当コードなし">R8点数</SortTh>
                <SortTh s={s} sortKey="gi" get={(r) => (r as SurgeryRpRow).gi_med} className="px-1.5 py-1 border-b text-right whitespace-nowrap" title="1件あたり手技金額 (円・薬剤材料費差引後)。※中央値 [Q1–Q3]"><span className="inline-block text-right leading-tight">手技金額<br /><span className="text-[9px] font-normal text-slate-400">1件あたり※</span></span></SortTh>
                <SortTh s={s} sortKey="zai" get={(r) => (r as SurgeryRpRow).zai_med} className="px-1.5 py-1 border-b text-right whitespace-nowrap" title="1件あたり材料金額 (円)。※中央値 [Q1–Q3]"><span className="inline-block text-right leading-tight">材料金額<br /><span className="text-[9px] font-normal text-slate-400">1件あたり※</span></span></SortTh>
                <SortTh s={s} sortKey="n" get={(r) => (r as SurgeryRpRow).n} className="px-1.5 py-1 border-b text-right whitespace-nowrap" title={`手術RPの件数の月平均 (会計単位。手術台帳の件数とは別物)。期間合計 ÷ ${periodMonths}ヶ月`}><span className="inline-block text-right leading-tight">手術会計件数<br /><span className="text-[9px] font-normal text-slate-400">月平均</span></span></SortTh>
                <SortTh s={s} sortKey="gisum" get={(r) => (r as SurgeryRpRow).gi_sum} className="px-1.5 py-1 border-b text-right whitespace-nowrap" title={`手技金額の月平均 (円・薬剤材料費差引後)。期間合計 ÷ ${periodMonths}ヶ月`}><span className="inline-block text-right leading-tight">手技金額<br /><span className="text-[9px] font-normal text-slate-400">月平均</span></span></SortTh>
                <SortTh s={s} sortKey="totalsum" get={(r) => (r as SurgeryRpRow).total_sum} className="px-1.5 py-1 border-b text-right whitespace-nowrap" title={`手技+材料+薬剤 の月平均 (円)。期間合計 ÷ ${periodMonths}ヶ月`}><span className="inline-block text-right leading-tight">総額<br /><span className="text-[9px] font-normal text-slate-400">月平均</span></span></SortTh>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !isLoading && (
                <tr><td colSpan={9} className="text-center text-slate-400 py-4">該当データなし</td></tr>
              )}
              {s.rows.map((r) => {
                const selected = selectedProcedureKeys.has(r.kcode);
                return (
                <tr key={r.kcode}
                  className={`border-b border-slate-100 cursor-pointer ${
                    selected ? "bg-amber-100 outline outline-2 outline-amber-300 font-semibold" : "hover:bg-blue-50"
                  }`}
                  onClick={(e) => selectProcedure(r.kcode, e.ctrlKey || e.metaKey)}
                  title="通常クリックで1術式を表示、Ctrl/⌘+クリックで複数選択"
                  role="button"
                  tabIndex={0}
                  aria-selected={selected}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter" && e.key !== " ") return;
                    e.preventDefault();
                    selectProcedure(r.kcode, e.ctrlKey || e.metaKey);
                  }}>
                  <td className="px-1.5 py-1 font-mono whitespace-nowrap" title={`配下レセ電コード: ${r.tencd.split(",").map((v) => v.replace(/^0+/, "")).join(", ")}`}>{r.kcode}</td>
                  <td className="px-1.5 py-1 leading-tight">
                    {selected && "✓ "}{r.kname}
                    {r.tencd_count > 1 && <span className="ml-1 text-[9px] text-slate-400 whitespace-nowrap">（{r.tencd_count}診療行為を集約）</span>}
                  </td>
                  <td className="px-1.5 py-1 text-right tabular-nums">{i0(r.ten2024)}</td>
                  <td className="px-1.5 py-1 text-right tabular-nums">{i0(r.ten2026)}</td>
                  <td className="px-1.5 py-1 text-right text-emerald-800"><MedIqr med={r.gi_med} q1={r.gi_q1} q3={r.gi_q3} /></td>
                  <td className="px-1.5 py-1 text-right"><MedIqr med={r.zai_med} q1={r.zai_q1} q3={r.zai_q3} /></td>
                  <td className="px-1.5 py-1 text-right tabular-nums font-semibold"
                    title={`期間の件数 ${r.n.toLocaleString()}件 ÷ ${periodMonths}ヶ月`}>
                    {(r.n / periodMonths).toFixed(periodMonths === 1 ? 0 : 1)}
                  </td>
                  <td className="px-1.5 py-1 text-right tabular-nums font-semibold text-emerald-800"
                    title={`期間合計 ${i0(r.gi_sum)}円 ÷ ${periodMonths}ヶ月`}>{i0(r.gi_sum / periodMonths)}</td>
                  <td className="px-1.5 py-1 text-right tabular-nums"
                    title={`期間合計 ${i0(r.total_sum)}円 ÷ ${periodMonths}ヶ月`}>{i0(r.total_sum / periodMonths)}</td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-400 mt-2">
          ※ = 中央値 [Q1–Q3]。手技 = 薬剤材料費差引後。金額・件数とも月平均 = 期間合計 ÷ {periodMonths}ヶ月。R6/R8 = 令和6/8年度改定の一般点数（空欄＝その年度のマスタに無い）。
        </p>
      </section>
    </div>
  );
}
